import type { z } from "zod";
import type {
    invoiceDtoSchema,
    invoiceLineItemResponseSchema,
} from "../schemas/domains/invoice/invoice-response.schema";
import { AccountOverrideEntry, ClassAssignment } from "./transaction";
import { Counterparty } from "./counterparty";
import { FinancialAccountBase } from "./financialAccount";

export enum InvoiceStatus {
    DRAFT = "draft",
    OPEN = "open",
    PAID = "paid",
    VOID = "void",
    UNCOLLECTABLE = "uncollectable",
}

export enum InvoiceSentStatus {
    SENT = "sent",
    NOT_SENT = "not_sent",
    SCHEDULED = "scheduled",
}

export enum InvoiceSource {
    MANUAL = "manual",
    STRIPE = "stripe",
    MERCURY = "mercury",
    IMPORTER = "importer",
    BILLCOM = "billcom",
    MCP = "mcp",
    QUICKBOOKS = "quickbooks",
}

// Distinguishes a regular invoice line from a synthetic line generated by
// splitting an invoice's header-level tax/discount/deposit across its item
// lines (see NativeInvoiceLineItemTaxDiscountSplitterInternalService). Native
// (MANUAL) writes and the QuickBooks invoice sync emit TAX/DISCOUNT/DEPOSIT
// rows; every other source only ever writes ITEM rows.
export enum InvoiceLineItemKind {
    ITEM = "item",
    TAX = "tax",
    DISCOUNT = "discount",
    DEPOSIT = "deposit",
}

export interface InvoiceLineItemAccountOverrides {
    accountOverrides?: AccountOverrideEntry[];
    accountsReceivableOverrides?: AccountOverrideEntry[];
}

/**
 * Base type for InvoiceLineItem - allows undefined on relations.
 * Used by entity classes (TypeORM).
 */
export interface InvoiceLineItemBase extends InvoiceLineItemAccountOverrides {
    id: string;
    amount: number;
    matchedAmount: number;
    unitAmount?: number | null;
    currency: string;
    description: string | null;
    quantityDecimal: number | null;
    source: InvoiceSource;
    sourceId: string;
    accountId?: string | null;
    accountsReceivableId?: string | null;
    classAssignments?: ClassAssignment[];
    invoiceId: string;
    invoice?: InvoiceBase;
    // "item" (default) for a regular line; "tax"/"discount" for a synthetic
    // line auto-generated by splitting a native invoice's header tax/discount
    // across its item lines. Every non-native source always writes "item".
    kind: InvoiceLineItemKind;
    // Parent item for a per-line tax, discount, or deposit row. Null on item
    // rows and on a document-level tax adjustment.
    rowId: string | null;
    // Carries the selected catalog item's name at time of save, used to power
    // the "Item" column. Written by native invoices, the importer and the
    // QuickBooks migration alike.
    nativeDetail?: { productOrService: string | null } | null;
}

/**
 * Base type for Invoice - allows undefined on relations.
 * Used by entity classes (TypeORM).
 */
export interface InvoiceBase {
    id: string;
    createdAt: Date;
    updatedAt: Date;
    due: Date | null;
    paidAt: Date | null;
    // Postgres `date` columns are read back as "YYYY-MM-DD" strings.
    uncollectableAt: string | null;
    voidedAt: string | null;
    // User-facing business date (YYYY-MM-DD) for native (MANUAL source)
    // invoices only; null for every other source.
    invoiceDate: string | null;
    dateOnly: string | null;
    paidDateOnly: string | null;
    dueDateOnly: string | null;
    total: number;
    subtotal: number;
    tax: number;
    discount: number;
    currency: string;
    counterparty?: Counterparty | null;
    description: string | null;
    memo: string | null;
    status: InvoiceStatus;
    externalStatus: InvoiceStatus | null;
    source: InvoiceSource;
    sourceId: string | null;
    isSubscription: boolean;
    lines?: InvoiceLineItemBase[];
    entityId: number;
    financialAccountId: number | null;
    financialAccount?: FinancialAccountBase | null;
    invoiceNumber: string | null;
    financialDocumentIds?: number[];
    // Revision lineage: when this invoice was created by revising an Open
    // invoice, points to that immediate predecessor. Null for originals.
    revisedFromInvoiceId?: string | null;
    templatePrototypeId?: string | null;
    sourceTemplateId?: string | null;
}

/**
 * Strict Invoice DTO shape, derived from the API response schema.
 */
export type Invoice = z.infer<typeof invoiceDtoSchema>;

/**
 * Strict InvoiceLineItem DTO shape, derived from the API response schema.
 * The parent back-reference is attached client-side, not sent on the wire.
 */
export type InvoiceLineItem = z.infer<typeof invoiceLineItemResponseSchema> & {
    invoice?: Invoice;
    // Set only on a collapsed header-level tax/discount/deposit row in the
    // by-line-item view: the ids of every underlying line the row represents, so
    // bulk coding updates all of them and not just the retained row's id.
    collapsedLineItemIds?: string[];
};

export interface InvoiceSort {
    field: string;
    order: "ASC" | "DESC";
}

export enum InvoiceSortValue {
    DATE_ASC = "DATE_ASC",
    DATE_DESC = "DATE_DESC",
    INVOICE_DATE_ASC = "INVOICE_DATE_ASC",
    INVOICE_DATE_DESC = "INVOICE_DATE_DESC",
    DUE_DATE_ASC = "DUE_DATE_ASC",
    DUE_DATE_DESC = "DUE_DATE_DESC",
    STATUS_ASC = "STATUS_ASC",
    STATUS_DESC = "STATUS_DESC",
    EXTERNAL_STATUS_ASC = "EXTERNAL_STATUS_ASC",
    EXTERNAL_STATUS_DESC = "EXTERNAL_STATUS_DESC",
    COUNTERPARTY_ASC = "COUNTERPARTY_ASC",
    COUNTERPARTY_DESC = "COUNTERPARTY_DESC",
    ACCOUNT_ASC = "ACCOUNT_ASC",
    ACCOUNT_DESC = "ACCOUNT_DESC",
    AMOUNT_ASC = "AMOUNT_ASC",
    AMOUNT_DESC = "AMOUNT_DESC",
    OPEN_ASC = "OPEN_ASC",
    OPEN_DESC = "OPEN_DESC",
    PAID_ASC = "PAID_ASC",
    PAID_DESC = "PAID_DESC",
    DAYS_OVERDUE_ASC = "DAYS_OVERDUE_ASC",
    DAYS_OVERDUE_DESC = "DAYS_OVERDUE_DESC",
    INVOICE_NUMBER_ASC = "INVOICE_NUMBER_ASC",
    INVOICE_NUMBER_DESC = "INVOICE_NUMBER_DESC",
    NONE = "NONE",
}

export enum InvoiceByCounterpartySortValue {
    COUNTERPARTY_ASC = "COUNTERPARTY_ASC",
    COUNTERPARTY_DESC = "COUNTERPARTY_DESC",
    CURRENT_ASC = "CURRENT_ASC",
    CURRENT_DESC = "CURRENT_DESC",
    PAST_DUE_1_30_ASC = "PAST_DUE_1_30_ASC",
    PAST_DUE_1_30_DESC = "PAST_DUE_1_30_DESC",
    PAST_DUE_31_60_ASC = "PAST_DUE_31_60_ASC",
    PAST_DUE_31_60_DESC = "PAST_DUE_31_60_DESC",
    PAST_DUE_61_90_ASC = "PAST_DUE_61_90_ASC",
    PAST_DUE_61_90_DESC = "PAST_DUE_61_90_DESC",
    PAST_DUE_91_PLUS_ASC = "PAST_DUE_91_PLUS_ASC",
    PAST_DUE_91_PLUS_DESC = "PAST_DUE_91_PLUS_DESC",
    OPEN_ASC = "OPEN_ASC",
    OPEN_DESC = "OPEN_DESC",
    PAID_ASC = "PAID_ASC",
    PAID_DESC = "PAID_DESC",
    INVOICES_COUNT_ASC = "INVOICES_COUNT_ASC",
    INVOICES_COUNT_DESC = "INVOICES_COUNT_DESC",
}

/**
 * 5-bucket aging scheme for the Invoicing tab, matching the AR Aging report's
 * bucket boundaries (Current / 1-30 / 31-60 / 61-90 / 91+). Unlike the AR
 * Aging report, these buckets are `NOW()`-based (not as-of-date); see
 * `resolveArAgingBucket` for the as-of-date variant used by reports.
 */
export enum InvoiceDueDateSubsegment {
    CURRENT = "current",
    PAST_DUE_1_TO_30 = "past_due_1_to_30",
    PAST_DUE_31_TO_60 = "past_due_31_to_60",
    PAST_DUE_61_TO_90 = "past_due_61_to_90",
    PAST_DUE_91_PLUS = "past_due_91_plus",
}

export const InvoiceDueDateLabels = {
    [InvoiceDueDateSubsegment.CURRENT]: "Current",
    [InvoiceDueDateSubsegment.PAST_DUE_1_TO_30]: "1-30",
    [InvoiceDueDateSubsegment.PAST_DUE_31_TO_60]: "31-60",
    [InvoiceDueDateSubsegment.PAST_DUE_61_TO_90]: "61-90",
    [InvoiceDueDateSubsegment.PAST_DUE_91_PLUS]: "91+",
};

export enum InvoiceViewOptionGroup {
    INVOICE = "invoice",
    COUNTERPARTY = "counterparty",
    LINE_ITEMS = "line_items",
}

export interface InvoiceViewOptions {
    groupBy: InvoiceViewOptionGroup;
}

export interface InvoiceStatEntity {
    id: number;
    count: number;
    total: number;
}

export interface InvoiceStatSubsegment {
    label: InvoiceDueDateSubsegment;
    count: number;
    total: number;
    entities?: InvoiceStatEntity[];
}

export interface InvoiceStat {
    count: number;
    total: number;
    subsegments?: InvoiceStatSubsegment[];
}

export interface InvoiceStats {
    draft: InvoiceStat;
    open: InvoiceStat;
    paid: InvoiceStat;
    void: InvoiceStat;
    uncollectable: InvoiceStat;
}

export interface InvoicesByCounterpartyItem {
    counterpartyId: string;
    counterparty?: Counterparty;
    open: number;
    paid: number;
    current: number;
    pastDue1To30: number;
    pastDue31To60: number;
    pastDue61To90: number;
    pastDue91Plus: number;
    invoiceCount: number;
    totalCounterparties: number;
    entityIds: number[];
}
